import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { UserStateService } from '../service/user-state.service';

export interface CacheEntry {
  response: HttpResponse<unknown>;
  timestamp: number;
  ttl: number;
}

const excludes = (req: HttpRequest<unknown>) => {
  return [
    'warehousing\\?filter',
    '/api/v1/compilation/counter_increase',
    '/api/v1/activity',
  ].some(c => new RegExp(c).test(req.urlWithParams))
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, Observable<HttpEvent<unknown>>>();

  private readonly defaultTtl = 60000; // 60 seconds
  private readonly maxCacheSize = 2000;

  private userState = inject(UserStateService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const user = this.userState.getUser();
    // user?.short === 'AR': no cache for Auto Repport / Weekly report mail
    if (user?.short === 'AR' || excludes(req) || req.method !== 'GET' && !/document_report/.test(req.urlWithParams)) {
      return next.handle(req);
    }

    const cacheKey = this.getCacheKey(req);
    const cached = this.cache.get(cacheKey);

    if (cached && !this.isExpired(cached)) {
      console.log(`[CacheInterceptor] HIT (${cached.ttl}ms TTL, age=${Date.now() - cached.timestamp}ms)`, cacheKey);
      return new Observable((observer) => {
        observer.next(cached.response);
        observer.complete();
      });
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      console.log(`[CacheInterceptor] DEDUP ${req.method} ${cacheKey}`);
      return pending;
    }

    const request$ = next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const ttl = this.getTtlForUrl(req.url);
          this.cache.set(cacheKey, {
            response: event,
            timestamp: Date.now(),
            ttl,
          });
        }
      }),
      shareReplay(1),
      catchError((error) => {
        console.log(`[CacheInterceptor] ERROR ${req.method} ${cacheKey}`, error);
        throw error;
      }),
      finalize(() => {
        this.pendingRequests.delete(cacheKey);
      })
    );

    this.pendingRequests.set(cacheKey, request$);
    return request$;
  }

  private getCacheKey(req: HttpRequest<unknown>): string {
    const body =
      req.body != null && ['POST', 'PUT', 'PATCH'].includes(req.method)
        ? `|${JSON.stringify(req.body)}`
        : '';

    return `${req.urlWithParams}${body}`;
  }

  private getTtlForUrl(url: string): number {
    if (url.includes('/api/v1/compilation/')) {
      return 60000;
    }
    if (url.includes('/api/v1/business') || url.includes('/api/v1/user') || url.includes('/api/v1/project')) {
      return 120000;
    }
    if (url.includes('/api/v1/report/')) {
      return 300000;
    }
    if (url.includes('/api/v1/document_report/')) {
      return 300000;
    }
    return this.defaultTtl;
  }

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  invalidateCacheForUrl(url: string): void {
    console.log('cache', this.cache);
    const prefix = url.split('?')[0];
    let count = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(prefix)) {
        this.cache.delete(key);
        count++;
      }
    }
    if (count > 0) {
      console.log(`[CacheInterceptor] INVALIDATE ${url} (${count} entries, remaining=${this.cache.size})`);
    }
  }

  clearCache(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`[CacheInterceptor] CLEAR ALL (${size} entries removed)`);
  }

  clearCacheForPattern(pattern: RegExp): void {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key);
        count++;
      }
    }
    if (count > 0) {
      console.log(`[CacheInterceptor] CLEAR PATTERN ${pattern} (${count} entries, remaining=${this.cache.size})`);
    }
  }
}